import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../services/apiClient';
import type { Tag } from '../../../features/tags/types';

/** useCloudMaterials / useTemporaryMaterials の最初のページから得られるファセット (両タブのマージ済み) */
export interface MergedFacets {
  source_types: string[];
  has_no_source_items: boolean;
  aspect_ratios: string[];
  brand_ids: string[];
  has_no_brand_items: boolean;
  has_no_product_items: boolean;
  tag_ids: string[];
  workflow_stage_ids: string[];
  has_no_workflow_stage_items: boolean;
}

export interface ProductOption {
  value: string;
  label: string;
  icon?: string;
  brand_id?: string;
}

interface UseFilterOptionsParams {
  brandFilter: string;
  productFilter: string;
  facets: MergedFacets | undefined;
  /** 組織のタグ一覧 (tagsQuery.data) */
  tags: Tag[] | undefined;
}

/**
 * フィルタドロップダウンの選択肢データを集約したフック。
 *
 * Amazon/Manual 商品・ブランド・お気に入りブランドを取得し、
 * ブランド⇔商品の相互絞り込みとファセットによる絞り込みを適用した
 * 選択肢リストを返す。
 */
export function useFilterOptions({ brandFilter, productFilter, facets, tags }: UseFilterOptionsParams) {
  // Amazon商品情報を取得
  const { data: amazonProductsData } = useQuery({
    queryKey: ['products', 'amazon', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('/amazon/products', {
        params: { limit: 1000 }
      });
      return response.data;
    },
  });

  // Manual商品情報を取得
  const { data: manualProductsData } = useQuery({
    queryKey: ['products', 'manual', 'all'],
    queryFn: async () => {
      const response = await apiClient.get('/products', {
        params: { limit: 1000 }
      });
      return response.data;
    },
  });

  // ブランド一覧を取得（brandsテーブルから）
  const { data: brandsData, isLoading: isLoadingBrands } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const response = await apiClient.get('/brands');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5分間はキャッシュを使用
  });

  // お気に入りブランドを取得（ブランドフィルタの並び順に使用）
  const { data: favoriteBrandsData } = useQuery({
    queryKey: ['user', 'favorite-brands'],
    queryFn: async () => {
      const response = await apiClient.get('/user/favorite-brands');
      return response.data as { favorites: { brand_id: string | null; brand_name: string | null }[] };
    },
    staleTime: 60_000,
  });

  // お気に入りキーの Set（brand_id を優先、なければ brand_name）
  const favoriteBrandKeys = useMemo(() => {
    const set = new Set<string>();
    for (const fav of favoriteBrandsData?.favorites || []) {
      const key = fav.brand_id || fav.brand_name;
      if (key) set.add(key);
    }
    return set;
  }, [favoriteBrandsData]);

  // 統合商品リスト（Amazon + Manual）
  const allProductOptions = useMemo<ProductOption[]>(() => {
    const options: ProductOption[] = [];

    // Amazon商品
    const amazonProducts = amazonProductsData?.products || [];
    amazonProducts.forEach((product: any) => {
      const title = product.title || product.asin || product.id;
      const asinSuffix = product.asin ? ` (${product.asin})` : '';
      options.push({ value: `amazon:${product.id}`, label: `${title}${asinSuffix}`, icon: '/amazon_icon.svg', brand_id: product.brand_id || undefined });
    });

    // Manual商品
    const manualProducts = manualProductsData?.products || [];
    manualProducts.forEach((product: any) => {
      const title = product.title || product.id;
      const skuSuffix = product.sku ? ` (${product.sku})` : '';
      options.push({ value: `manual:${product.id}`, label: `${title}${skuSuffix}`, icon: '/e_commerce_touch_buy.svg', brand_id: product.brand_id || undefined });
    });

    return options;
  }, [amazonProductsData, manualProductsData]);

  // brandsテーブルからブランドリストを取得
  const allBrands = useMemo<Array<{ brand_id: string; label: string }>>(() => {
    const brands = brandsData?.brands || [];
    return brands.map((brand: any) => ({
      brand_id: brand.id,
      label: brand.brand_name
    }));
  }, [brandsData]);

  // ブランド選択で商品を絞り込み、商品選択でブランドを絞り込み
  const filteredProductOptions = useMemo(() => {
    if (!brandFilter) return allProductOptions;
    if (brandFilter === '__no_brand__') return allProductOptions.filter(opt => !opt.brand_id);
    return allProductOptions.filter(opt => opt.brand_id === brandFilter);
  }, [allProductOptions, brandFilter]);

  const filteredBrandOptions = useMemo(() => {
    let brands = allBrands;
    // 商品選択によるブランド絞り込み
    if (productFilter && productFilter !== '__no_product__') {
      const selectedProduct = allProductOptions.find(opt => opt.value === productFilter);
      if (selectedProduct?.brand_id) {
        brands = brands.filter(b => b.brand_id === selectedProduct.brand_id);
      }
    }
    // ファセットデータによる絞り込み（利用可能なブランドのみ表示）
    if (facets?.brand_ids) {
      const availableSet = new Set(facets.brand_ids);
      brands = brands.filter(b => availableSet.has(b.brand_id));
    }
    // お気に入りを上位に固める（同グループ内は元の順序を維持＝stable sort）
    return brands
      .map((b, index) => ({ ...b, isFavorite: favoriteBrandKeys.has(b.brand_id), index }))
      .sort((a, b) => {
        if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
        return a.index - b.index;
      });
  }, [allBrands, productFilter, allProductOptions, facets, favoriteBrandKeys]);

  // 組織のタグ一覧からドロップダウン選択肢を生成（ファセットで絞り込み）
  const allTags = useMemo<string[]>(() => {
    if (!tags) return [];
    if (!facets?.tag_ids || facets.tag_ids.length === 0) return [];
    const facetTagSet = new Set(facets.tag_ids);
    return tags
      .filter(t => facetTagSet.has(t.id))
      .map(t => t.name);
  }, [tags, facets]);

  return {
    amazonProductsData,
    allProductOptions,
    filteredProductOptions,
    filteredBrandOptions,
    isLoadingBrands,
    allTags,
  };
}

export type FilterOptionsApi = ReturnType<typeof useFilterOptions>;
